import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft, FileText, Shield, CreditCard, Truck, AlertTriangle, Scale } from 'lucide-react';
import { Button } from '../components/ui/button';

const CGUPage = () => {
  const lastUpdate = "6 février 2025";

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Header */}
      <header className="bg-white border-b border-slate-200 sticky top-0 z-50">
        <div className="max-w-4xl mx-auto px-4 py-4 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2 text-slate-600 hover:text-slate-900">
            <ArrowLeft className="w-5 h-5" />
            Retour à l&apos;accueil
          </Link>
          <Link to="/" className="font-['Barlow_Condensed'] text-2xl font-bold text-orange-500">
            COLISFLASH
          </Link>
        </div>
      </header>

      {/* Content */}
      <main className="max-w-4xl mx-auto px-4 py-12">
        <div className="bg-white rounded-2xl shadow-sm border border-slate-200 p-8 md:p-12">
          {/* Title */}
          <div className="text-center mb-12">
            <div className="inline-flex items-center justify-center w-16 h-16 bg-orange-100 rounded-2xl mb-4">
              <FileText className="w-8 h-8 text-orange-500" />
            </div>
            <h1 className="font-['Barlow_Condensed'] text-4xl font-bold text-slate-900 mb-2">
              Conditions Générales d&apos;Utilisation
            </h1>
            <p className="text-slate-500">Dernière mise à jour : {lastUpdate}</p>
          </div>

          {/* Table of Contents */}
          <div className="bg-slate-50 rounded-xl p-6 mb-10">
            <h2 className="font-semibold text-slate-900 mb-4">Sommaire</h2>
            <nav className="space-y-2 text-sm">
              <a href="#article-1" className="block text-orange-600 hover:text-orange-700">1. Objet et champ d&apos;application</a>
              <a href="#article-2" className="block text-orange-600 hover:text-orange-700">2. Définitions</a>
              <a href="#article-3" className="block text-orange-600 hover:text-orange-700">3. Inscription et compte utilisateur</a>
              <a href="#article-4" className="block text-orange-600 hover:text-orange-700">4. Fonctionnement du service</a>
              <a href="#article-5" className="block text-orange-600 hover:text-orange-700">5. Tarification et paiement</a>
              <a href="#article-6" className="block text-orange-600 hover:text-orange-700">6. Responsabilités</a>
              <a href="#article-7" className="block text-orange-600 hover:text-orange-700">7. Assurance et garanties</a>
              <a href="#article-8" className="block text-orange-600 hover:text-orange-700">8. Protection des données personnelles</a>
              <a href="#article-9" className="block text-orange-600 hover:text-orange-700">9. Propriété intellectuelle</a>
              <a href="#article-10" className="block text-orange-600 hover:text-orange-700">10. Modification des CGU</a>
              <a href="#article-11" className="block text-orange-600 hover:text-orange-700">11. Droit applicable et litiges</a>
            </nav>
          </div>

          {/* Articles */}
          <div className="prose prose-slate max-w-none">
            
            {/* Article 1 */}
            <section id="article-1" className="mb-10">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-10 h-10 bg-orange-100 rounded-lg flex items-center justify-center">
                  <FileText className="w-5 h-5 text-orange-500" />
                </div>
                <h2 className="text-2xl font-bold text-slate-900 m-0">1. Objet et champ d&apos;application</h2>
              </div>
              <p className="text-slate-600 leading-relaxed">
                Les présentes Conditions Générales d&apos;Utilisation (ci-après « CGU ») régissent l&apos;utilisation de la plateforme COLISFLASH, 
                accessible à l&apos;adresse <strong>colisflash.com</strong>, qui met en relation des particuliers souhaitant envoyer des colis 
                (« Expéditeurs ») avec des particuliers effectuant un trajet et disposés à transporter ces colis (« Cotransporteurs »).
              </p>
              <p className="text-slate-600 leading-relaxed">
                L&apos;utilisation de la plateforme implique l&apos;acceptation pleine et entière des présentes CGU. Si vous n&apos;acceptez pas ces conditions, 
                veuillez ne pas utiliser nos services.
              </p>
            </section>

            {/* Article 2 */}
            <section id="article-2" className="mb-10">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center">
                  <Scale className="w-5 h-5 text-blue-500" />
                </div>
                <h2 className="text-2xl font-bold text-slate-900 m-0">2. Définitions</h2>
              </div>
              <ul className="text-slate-600 space-y-3">
                <li><strong>Plateforme :</strong> Le site web et l&apos;application COLISFLASH permettant la mise en relation.</li>
                <li><strong>Utilisateur :</strong> Toute personne inscrite sur la plateforme, qu&apos;elle soit Expéditeur ou Cotransporteur.</li>
                <li><strong>Expéditeur :</strong> Utilisateur souhaitant faire transporter un colis.</li>
                <li><strong>Cotransporteur :</strong> Utilisateur effectuant un trajet et proposant de transporter des colis.</li>
                <li><strong>Mission :</strong> Le transport d&apos;un colis par un Cotransporteur pour le compte d&apos;un Expéditeur.</li>
                <li><strong>Commission :</strong> Frais prélevés par COLISFLASH sur chaque transaction (15% du prix convenu).</li>
              </ul>
            </section>

            {/* Article 3 */}
            <section id="article-3" className="mb-10">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-10 h-10 bg-green-100 rounded-lg flex items-center justify-center">
                  <Shield className="w-5 h-5 text-green-500" />
                </div>
                <h2 className="text-2xl font-bold text-slate-900 m-0">3. Inscription et compte utilisateur</h2>
              </div>
              <h3 className="text-lg font-semibold text-slate-800 mt-6">3.1 Conditions d&apos;inscription</h3>
              <p className="text-slate-600 leading-relaxed">
                Pour utiliser COLISFLASH, vous devez :
              </p>
              <ul className="text-slate-600 space-y-2">
                <li>Être une personne physique majeure (18 ans ou plus)</li>
                <li>Fournir des informations exactes et complètes lors de l&apos;inscription</li>
                <li>Disposer d&apos;une adresse email valide</li>
                <li>Accepter les présentes CGU</li>
              </ul>
              
              <h3 className="text-lg font-semibold text-slate-800 mt-6">3.2 Vérification d&apos;identité</h3>
              <p className="text-slate-600 leading-relaxed">
                COLISFLASH propose un service de vérification d&apos;identité via Stripe Identity. Cette vérification, bien que facultative, 
                permet d&apos;obtenir un badge « Vérifié » sur votre profil et renforce la confiance entre utilisateurs.
              </p>
              
              <h3 className="text-lg font-semibold text-slate-800 mt-6">3.3 Sécurité du compte</h3>
              <p className="text-slate-600 leading-relaxed">
                Vous êtes responsable de la confidentialité de vos identifiants de connexion. Toute activité réalisée depuis votre compte 
                est présumée être de votre fait. En cas de suspicion d&apos;utilisation frauduleuse, contactez-nous immédiatement.
              </p>
            </section>

            {/* Article 4 */}
            <section id="article-4" className="mb-10">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-10 h-10 bg-purple-100 rounded-lg flex items-center justify-center">
                  <Truck className="w-5 h-5 text-purple-500" />
                </div>
                <h2 className="text-2xl font-bold text-slate-900 m-0">4. Fonctionnement du service</h2>
              </div>
              
              <h3 className="text-lg font-semibold text-slate-800 mt-6">4.1 Pour les Expéditeurs</h3>
              <ul className="text-slate-600 space-y-2">
                <li>Créer une annonce décrivant le colis à transporter (dimensions, poids, description)</li>
                <li>Indiquer les adresses d&apos;enlèvement et de livraison</li>
                <li>Proposer un budget pour le transport</li>
                <li>Payer le montant convenu avant la prise en charge du colis</li>
              </ul>
              
              <h3 className="text-lg font-semibold text-slate-800 mt-6">4.2 Pour les Cotransporteurs</h3>
              <ul className="text-slate-600 space-y-2">
                <li>Publier vos trajets avec les dates et itinéraires</li>
                <li>Consulter les colis disponibles sur votre route</li>
                <li>Faire des offres de prix aux Expéditeurs</li>
                <li>Négocier le tarif via le système de contre-offres</li>
                <li>Prendre en charge et livrer le colis dans les délais convenus</li>
              </ul>
              
              <h3 className="text-lg font-semibold text-slate-800 mt-6">4.3 Objets interdits</h3>
              <p className="text-slate-600 leading-relaxed">
                Sont strictement interdits au transport :
              </p>
              <ul className="text-slate-600 space-y-2">
                <li>Produits illicites, stupéfiants, armes</li>
                <li>Matières dangereuses, explosives ou inflammables</li>
                <li>Animaux vivants</li>
                <li>Denrées périssables sans emballage adapté</li>
                <li>Objets volés ou contrefaits</li>
                <li>Tout objet dont le transport est interdit par la loi</li>
              </ul>
            </section>

            {/* Article 5 */}
            <section id="article-5" className="mb-10">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-10 h-10 bg-yellow-100 rounded-lg flex items-center justify-center">
                  <CreditCard className="w-5 h-5 text-yellow-600" />
                </div>
                <h2 className="text-2xl font-bold text-slate-900 m-0">5. Tarification et paiement</h2>
              </div>
              
              <h3 className="text-lg font-semibold text-slate-800 mt-6">5.1 Négociation du prix</h3>
              <p className="text-slate-600 leading-relaxed">
                Le prix du transport est librement négocié entre l&apos;Expéditeur et le Cotransporteur via notre système d&apos;offres et contre-offres.
              </p>
              
              <h3 className="text-lg font-semibold text-slate-800 mt-6">5.2 Commission COLISFLASH</h3>
              <p className="text-slate-600 leading-relaxed">
                COLISFLASH prélève une commission de <strong>15%</strong> sur le prix convenu entre les parties. 
                Cette commission couvre les frais de fonctionnement de la plateforme, le support client et les services de mise en relation.
              </p>
              
              <h3 className="text-lg font-semibold text-slate-800 mt-6">5.3 Paiement sécurisé</h3>
              <p className="text-slate-600 leading-relaxed">
                Tous les paiements sont effectués via notre partenaire <strong>Stripe</strong>. L&apos;Expéditeur paie le montant total 
                (prix + commission) lors de la validation de la mission. Le Cotransporteur reçoit son versement après confirmation de la livraison.
              </p>
              
              <h3 className="text-lg font-semibold text-slate-800 mt-6">5.4 Versement aux Cotransporteurs</h3>
              <p className="text-slate-600 leading-relaxed">
                Les versements sont effectués par virement bancaire sur l&apos;IBAN renseigné dans votre profil, après validation de la livraison 
                par l&apos;Expéditeur. Le délai de versement est généralement de 3 à 7 jours ouvrés.
              </p>
            </section>

            {/* Article 6 */}
            <section id="article-6" className="mb-10">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-10 h-10 bg-red-100 rounded-lg flex items-center justify-center">
                  <AlertTriangle className="w-5 h-5 text-red-500" />
                </div>
                <h2 className="text-2xl font-bold text-slate-900 m-0">6. Responsabilités</h2>
              </div>
              
              <h3 className="text-lg font-semibold text-slate-800 mt-6">6.1 Rôle de COLISFLASH</h3>
              <p className="text-slate-600 leading-relaxed">
                COLISFLASH agit en qualité d&apos;intermédiaire et met en relation Expéditeurs et Cotransporteurs. 
                COLISFLASH n&apos;est pas partie au contrat de transport conclu entre les utilisateurs et ne peut être tenu responsable 
                de l&apos;exécution de ce contrat.
              </p>
              
              <h3 className="text-lg font-semibold text-slate-800 mt-6">6.2 Responsabilité de l&apos;Expéditeur</h3>
              <ul className="text-slate-600 space-y-2">
                <li>Décrire exactement le contenu du colis</li>
                <li>Emballer correctement le colis pour le transport</li>
                <li>Ne pas envoyer d&apos;objets interdits</li>
                <li>Être présent ou représenté lors de l&apos;enlèvement</li>
              </ul>
              
              <h3 className="text-lg font-semibold text-slate-800 mt-6">6.3 Responsabilité du Cotransporteur</h3>
              <ul className="text-slate-600 space-y-2">
                <li>Transporter le colis avec soin</li>
                <li>Respecter les délais convenus</li>
                <li>Remettre le colis en bon état au destinataire</li>
                <li>Signaler tout incident ou retard</li>
              </ul>
            </section>

            {/* Article 7 */}
            <section id="article-7" className="mb-10">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-10 h-10 bg-indigo-100 rounded-lg flex items-center justify-center">
                  <Shield className="w-5 h-5 text-indigo-500" />
                </div>
                <h2 className="text-2xl font-bold text-slate-900 m-0">7. Assurance et garanties</h2>
              </div>
              <p className="text-slate-600 leading-relaxed">
                COLISFLASH recommande vivement aux Expéditeurs de souscrire une assurance pour les objets de valeur. 
                Le Cotransporteur est encouragé à vérifier que son assurance automobile couvre le transport de colis pour compte d&apos;autrui.
              </p>
              <p className="text-slate-600 leading-relaxed">
                En cas de litige concernant un colis endommagé ou perdu, COLISFLASH pourra servir de médiateur entre les parties 
                mais ne pourra être tenu pour responsable des dommages.
              </p>
            </section>

            {/* Article 8 */}
            <section id="article-8" className="mb-10">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-10 h-10 bg-cyan-100 rounded-lg flex items-center justify-center">
                  <Shield className="w-5 h-5 text-cyan-500" />
                </div>
                <h2 className="text-2xl font-bold text-slate-900 m-0">8. Protection des données personnelles</h2>
              </div>
              <p className="text-slate-600 leading-relaxed">
                COLISFLASH collecte et traite vos données personnelles conformément au Règlement Général sur la Protection des Données (RGPD). 
                Pour plus d&apos;informations, consultez notre <Link to="/privacy" className="text-orange-600 hover:underline">Politique de Confidentialité</Link>.
              </p>
              <p className="text-slate-600 leading-relaxed">
                Les données collectées incluent : nom, prénom, email, téléphone, adresses, données de paiement (traitées par Stripe), 
                et le cas échéant, les données de vérification d&apos;identité.
              </p>
            </section>

            {/* Article 9 */}
            <section id="article-9" className="mb-10">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-10 h-10 bg-pink-100 rounded-lg flex items-center justify-center">
                  <FileText className="w-5 h-5 text-pink-500" />
                </div>
                <h2 className="text-2xl font-bold text-slate-900 m-0">9. Propriété intellectuelle</h2>
              </div>
              <p className="text-slate-600 leading-relaxed">
                L&apos;ensemble des éléments de la plateforme COLISFLASH (logo, design, textes, fonctionnalités) sont protégés par les droits 
                de propriété intellectuelle. Toute reproduction ou utilisation non autorisée est interdite.
              </p>
            </section>

            {/* Article 10 */}
            <section id="article-10" className="mb-10">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-10 h-10 bg-orange-100 rounded-lg flex items-center justify-center">
                  <FileText className="w-5 h-5 text-orange-500" />
                </div>
                <h2 className="text-2xl font-bold text-slate-900 m-0">10. Modification des CGU</h2>
              </div>
              <p className="text-slate-600 leading-relaxed">
                COLISFLASH se réserve le droit de modifier les présentes CGU à tout moment. Les utilisateurs seront informés par email 
                de toute modification substantielle. La continuation de l&apos;utilisation du service après modification vaut acceptation des nouvelles CGU.
              </p>
            </section>

            {/* Article 11 */}
            <section id="article-11" className="mb-10">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-10 h-10 bg-slate-100 rounded-lg flex items-center justify-center">
                  <Scale className="w-5 h-5 text-slate-500" />
                </div>
                <h2 className="text-2xl font-bold text-slate-900 m-0">11. Droit applicable et litiges</h2>
              </div>
              <p className="text-slate-600 leading-relaxed">
                Les présentes CGU sont régies par le droit français. En cas de litige, les parties s&apos;engagent à rechercher une solution amiable. 
                À défaut, les tribunaux français seront seuls compétents.
              </p>
              <p className="text-slate-600 leading-relaxed">
                Pour toute question concernant ces CGU, contactez-nous à : <strong>contact@colisflash.com</strong>
              </p>
            </section>

          </div>

          {/* Footer */}
          <div className="mt-12 pt-8 border-t border-slate-200 text-center">
            <p className="text-slate-500 text-sm">
              © {new Date().getFullYear()} COLISFLASH - Tous droits réservés
            </p>
            <div className="mt-4">
              <Link to="/">
                <Button className="bg-orange-500 hover:bg-orange-600">
                  Retour à l&apos;accueil
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </main>
    </div>
  );
};

export default CGUPage;
